<template>
  <div v-if="isDevelopment" class="fixed bottom-4 right-4 z-50">
    <div class="bg-white rounded-lg shadow-xl border-2 border-yellow-400 p-4 max-w-sm">
      <!-- 标题栏 -->
      <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <span class="text-lg">🧪</span>
          <span class="text-sm font-semibold text-gray-700">霍兰德测试工具</span>
        </div>
        <button
          @click="isExpanded = !isExpanded"
          class="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
            :class="{ 'rotate-180': isExpanded }"
            class="transition-transform"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>

      <!-- 展开内容 -->
      <div v-if="isExpanded" class="space-y-3">
        <!-- 快速选择(喜欢/不喜欢) -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-2">
            快速全部填充
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button
              @click="fillAllWithChoice(1)"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors bg-psychology-success-10 hover:bg-psychology-success-20 text-psychology-success"
            >
              👍 全部喜欢
            </button>
            <button
              @click="fillAllWithChoice(0)"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors bg-red-100 hover:bg-red-200 text-red-700"
            >
              👎 全部不喜欢
            </button>
          </div>
        </div>

        <!-- 选择霍兰德类型 -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-2">
            按霍兰德类型匹配答案
          </label>
          <select
            v-model="selectedType"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
          >
            <option value="">-- 选择类型 --</option>
            <option
              v-for="type in hollandTypeList"
              :key="type.code"
              :value="type.code"
            >
              {{ type.emoji }} {{ type.name }} - {{ type.description }}
            </option>
          </select>
          <button
            @click="fillWithHollandType"
            :disabled="!selectedType"
            class="mt-2 w-full px-3 py-2 text-sm font-medium rounded-md transition-colors bg-psychology-warning hover:bg-psychology-warning/90 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            填充答案
          </button>
        </div>

        <!-- 随机填充 -->
        <div>
          <button
            @click="fillRandom"
            class="w-full px-3 py-2 text-sm font-medium rounded-md transition-colors bg-psychology-primary hover:bg-psychology-primary/90 text-white"
          >
            🎲 随机填充
          </button>
        </div>

        <!-- 快速提交 -->
        <div class="pt-2 border-t border-gray-200">
          <button
            @click="quickSubmit"
            :disabled="answeredCount < 90"
            class="w-full px-3 py-2 text-sm font-medium rounded-md transition-colors bg-psychology-primary hover:bg-psychology-primary/90 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {{ answeredCount < 90 ? `快速提交 (${answeredCount}/90)` : '快速提交 ✓' }}
          </button>
        </div>

        <!-- 当前状态 -->
        <div class="pt-2 border-t border-gray-200">
          <div class="text-xs text-gray-500 space-y-1">
            <div>已答题: {{ answeredCount }}/90</div>
            <div>当前维度: {{ currentDimension }}/6 ({{ currentDimensionName }})</div>
            <div>当前题目: {{ currentQuestionInDimension }}/15</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getAllHollandTypes, 
  getHollandTypeInfo,
  generateAnswersForHollandType,
  generateAnswersForChoice,
  generateRandomAnswers
} from '@/utils/testHelper'
import { DIMENSIONS } from '@/data/hollandQuestions'
import { useToast } from 'vue-toastification'

export default {
  name: 'TestQuickFill',
  props: {
    answers: {
      type: Array,
      required: true
    },
    currentDimension: {
      type: Number,
      required: true
    },
    currentQuestionInDimension: {
      type: Number,
      required: true
    }
  },
  emits: ['update-answers', 'submit'],
  data() {
    return {
      isExpanded: true,
      selectedType: '',
      hollandTypeList: []
    }
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    answeredCount() {
      return this.answers.length
    },
    currentDimensionName() {
      const dimensionCodes = ['R', 'I', 'A', 'S', 'E', 'C']
      const dimensionCode = dimensionCodes[this.currentDimension] || 'R'
      return DIMENSIONS[dimensionCode]?.name || '未知'
    }
  },
  mounted() {
    this.hollandTypeList = getAllHollandTypes()
  },
  methods: {
    /**
     * 快速填充所有答案为指定选择
     */
    fillAllWithChoice(choice) {
      try {
        const newAnswers = generateAnswersForChoice(choice)
        this.$emit('update-answers', newAnswers)
        
        const toast = useToast()
        toast.success(`已填充所有答案为 ${choice === 1 ? '👍 喜欢' : '👎 不喜欢'}`)
      } catch (error) {
        const toast = useToast()
        toast.error(`填充失败: ${error.message}`)
      }
    },
    
    /**
     * 根据选择的霍兰德类型填充答案
     */
    fillWithHollandType() {
      if (!this.selectedType) {
        const toast = useToast()
        toast.warning('请先选择霍兰德类型')
        return
      }
      
      try {
        const newAnswers = generateAnswersForHollandType(this.selectedType)
        this.$emit('update-answers', newAnswers)
        
        const typeInfo = getHollandTypeInfo(this.selectedType)
        const toast = useToast()
        toast.success(`已根据 ${typeInfo.emoji} ${typeInfo.name} 填充答案`)
      } catch (error) {
        const toast = useToast()
        toast.error(`填充失败: ${error.message}`)
      }
    },
    
    /**
     * 随机填充答案
     */
    fillRandom() {
      try {
        const newAnswers = generateRandomAnswers()
        this.$emit('update-answers', newAnswers)
        
        const toast = useToast()
        toast.success('已随机填充所有答案')
      } catch (error) {
        const toast = useToast()
        toast.error(`填充失败: ${error.message}`)
      }
    },
    
    /**
     * 快速提交
     */
    quickSubmit() {
      if (this.answeredCount < 90) {
        const toast = useToast()
        toast.warning(`还有 ${90 - this.answeredCount} 道题未作答`)
        return
      }
      
      this.$emit('submit')
    }
  }
}
</script>

<style scoped>
/* 组件样式 */
</style>
